<script setup lang="ts">
/**
 * /actions/:name — one action: what it does, its steps (executor, capability,
 * error handling), where it is enabled, a run form (?target= and ?params= to
 * prefill, e.g. from "Run again"), its run history and linked knowledge pages.
 */
import { BookOpen, Code2, Eye, FileCode2, History, ListOrdered, MapPin, Play, SlidersHorizontal, Zap } from "@lucide/vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useKnowledgeSearch } from "@/api/knowledge";
import { actionTag, decodeParams, groupTargets, useRunbooks, useRuns } from "@/api/runbooks";
import RunActionForm from "@/components/actions/RunActionForm.vue";
import RunList from "@/components/actions/RunList.vue";
import TargetChip from "@/components/actions/TargetChip.vue";
import CopyField from "@/components/common/CopyField.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { entityPath } from "@/lib/knowledge";
import { labelFor, normalize, orderedProperties } from "@/lib/schema";

definePage({ meta: { title: "Action" } });

const route = useRoute("/actions/[name]");
const { can, allows } = useSession();
const name = computed(() => route.params.name);
const runbooks = useRunbooks();
const action = computed(() => runbooks.data.value?.find((a) => a.name === name.value) ?? null);
const target = computed(() => (typeof route.query.target === "string" ? route.query.target : undefined));
const params = computed(() => decodeParams(route.query.params));

const params_ = computed(() => (action.value ? orderedProperties(normalize(action.value.params).schema) : []));
const required = computed(() => new Set(action.value?.params.required ?? []));
const groups = computed(() => groupTargets(action.value?.enabledTargets ?? []));

// History
const stateFilter = useRouteQuery("state");
const targetFilter = useRouteQuery("runsOn");
const STATE_OPTIONS = ["succeeded", "failed", "completed_with_errors", "running", "awaiting_approval", "declined"].map((s) => ({ value: s, label: t.actions.state[s] }));
const runs = useRuns(
  () => ({ runbook: name.value, states: stateFilter.value ? [stateFilter.value] : undefined, target: targetFilter.value || undefined }),
  () => Boolean(name.value),
);
const runItems = computed(() => runs.data.value?.pages.flatMap((p) => p.items) ?? []);
const scope = computed(() => runs.data.value?.pages[0]?.scope);
const stateModel = computed({ get: () => stateFilter.value || undefined, set: (v: string | string[] | undefined) => (stateFilter.value = (v as string) ?? "") });
const targetModel = computed({ get: () => targetFilter.value || undefined, set: (v: string | string[] | undefined) => (targetFilter.value = (v as string) ?? "") });

// Knowledge pages tagged action-<name>
const tag = computed(() => actionTag(name.value));
const knowledge = useKnowledgeSearch(() => ({ q: "", types: can("knowledge.read") ? ["runbook"] : [], tags: [tag.value], limit: 10 }));
const linked = computed(() => (knowledge.data.value?.hits ?? []).filter((h) => h.kind === "entity" && h.type && h.slug));

const typeLabel = (schema: Record<string, unknown>) => {
  const s = normalize(schema).schema;
  if (s.enum) return (s.enum as unknown[]).map(String).join(" | ");
  if (s.type === "number" || s.type === "integer") return [s.minimum, s.maximum].every((x) => x !== undefined) ? `${s.type} ${s.minimum}–${s.maximum}` : String(s.type);
  return String(s.type ?? "value");
};

function scrollToRun() {
  const el = document.getElementById("run");
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
  el?.querySelector("button")?.focus({ preventScroll: true });
}
</script>

<template>
  <div>
    <ListSkeleton v-if="runbooks.isPending.value" :rows="5" />
    <ErrorState v-else-if="runbooks.error.value" :error="runbooks.error.value" @retry="runbooks.refetch()" />
    <template v-else-if="!action">
      <PageHeader :title="name" :crumbs="[{ label: t.actions.title, to: '/actions' }, { label: name }]" />
      <EmptyState :icon="Zap" :title="t.actions.unavailable" :description="t.actions.unavailableHint">
        <Button as-child variant="outline"><RouterLink to="/actions">{{ t.actions.all }}</RouterLink></Button>
        <Button v-if="allows({ adminArea: 'runbooks' })" as-child variant="ghost"><RouterLink :to="{ path: '/admin/runbooks', hash: `#${name}` }">Admin</RouterLink></Button>
      </EmptyState>
    </template>

    <template v-else>
      <PageHeader :title="action.title" :description="action.description" :crumbs="[{ label: t.actions.title, to: '/actions' }, { label: action.title }]">
        <template #badges>
          <Badge v-if="action.readOnly" variant="info"><Eye aria-hidden="true" /> {{ t.actions.readOnly }}</Badge>
          <Badge v-else variant="warning"><Zap aria-hidden="true" /> {{ t.actions.changes }}</Badge>
        </template>
        <template #meta>
          <dl class="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div class="flex gap-1.5"><dt class="text-muted-foreground">Name</dt><dd class="font-mono">{{ action.name }}</dd></div>
            <div class="flex gap-1.5"><dt class="text-muted-foreground">{{ t.actions.capability }}</dt><dd class="font-mono">{{ action.capability }}</dd></div>
            <div class="flex gap-1.5"><dt class="text-muted-foreground">Runs on</dt><dd class="font-mono">{{ action.targetType }}</dd></div>
          </dl>
        </template>
        <template #actions>
          <Button @click="scrollToRun"><Play /> {{ t.actions.run }}</Button>
        </template>
      </PageHeader>

      <div class="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div class="flex min-w-0 flex-col gap-6">
          <SectionCard :title="t.actions.steps" :icon="ListOrdered">
            <template #badge><Badge variant="muted">{{ action.steps.length }}</Badge></template>
            <ol class="flex flex-col gap-2">
              <li v-for="(s, i) in action.steps" :key="i" class="surface-inset flex gap-3 p-3">
                <span class="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary tabular-nums">{{ i + 1 }}</span>
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="font-medium">{{ s.title ?? s.summary }}</p>
                    <Badge v-if="s.mutates === false" variant="info"><Eye aria-hidden="true" /> {{ t.actions.readOnly }}</Badge>
                  </div>
                  <p v-if="s.title && s.summary !== s.title" class="mt-0.5 text-sm text-muted-foreground">{{ s.summary }}</p>
                  <div class="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                    <Badge variant="muted" class="font-mono">{{ s.executor }}</Badge>
                    <Badge v-if="s.capability" variant="outline" class="font-mono" :title="`${t.actions.capability} ${s.capability} on the target`">{{ s.capability }}</Badge>
                    <span class="text-muted-foreground">·</span>
                    <span :class="s.continueOnError ? 'text-muted-foreground' : 'text-foreground/80'">{{ s.continueOnError ? t.actions.continueOnError : t.actions.stopsOnError }}</span>
                  </div>
                </div>
              </li>
            </ol>
          </SectionCard>

          <SectionCard :title="t.actions.runHistory" :icon="History" :description="scope === 'mine' ? t.actions.othersHidden : undefined">
            <div class="mb-3 flex flex-col gap-2">
              <FilterChips v-model="stateModel" :options="STATE_OPTIONS" label="State" />
              <FilterChips v-if="action.enabledTargets.length > 1" v-model="targetModel" :options="action.enabledTargets.map((x) => ({ value: x, label: x }))" label="Target" />
            </div>
            <ListSkeleton v-if="runs.isPending.value" :rows="3" />
            <ErrorState v-else-if="runs.error.value" :error="runs.error.value" @retry="runs.refetch()" />
            <EmptyState v-else-if="runItems.length === 0" compact :icon="History" :title="t.actions.noRuns" :description="stateFilter || targetFilter ? undefined : t.actions.noRunsHint" />
            <template v-else>
              <RunList :runs="runItems" />
              <div v-if="runs.hasNextPage.value" class="mt-3 flex justify-center">
                <Button variant="outline" size="sm" :disabled="runs.isFetchingNextPage.value" @click="runs.fetchNextPage()">{{ t.actions.loadMore }}</Button>
              </div>
            </template>
          </SectionCard>
        </div>

        <aside class="flex min-w-0 flex-col gap-6">
          <SectionCard id="run" :title="t.actions.runIt" :icon="Play" class="scroll-mt-20">
            <p v-if="action.enabledTargets.length === 0" class="text-sm text-muted-foreground">{{ t.actions.notEnabled }}</p>
            <RunActionForm v-else :action="action" :target="target" :params="params" id-prefix="detail" />
          </SectionCard>

          <SectionCard :title="t.actions.enabledOn" :icon="MapPin" :description="t.actions.enabledOnHint">
            <p v-if="groups.length === 0" class="text-sm text-muted-foreground">{{ t.actions.notEnabled }}</p>
            <dl v-else class="flex flex-col gap-2.5">
              <div v-for="g in groups" :key="g.lineage" class="flex flex-col gap-1">
                <dt class="text-xs font-medium text-muted-foreground">{{ g.lineage }}</dt>
                <dd class="flex flex-wrap gap-1.5">
                  <TargetChip v-for="tg in g.targets" :key="tg.id" :target="tg.id" env-only :to="`/actions?target=${encodeURIComponent(tg.id)}`" />
                </dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard :title="t.actions.parameters" :icon="SlidersHorizontal">
            <p v-if="params_.length === 0" class="text-sm text-muted-foreground">{{ t.actions.noParameters }}</p>
            <dl v-else class="flex flex-col gap-3 text-sm">
              <div v-for="[pname, p] in params_" :key="pname">
                <dt class="flex flex-wrap items-center gap-1.5">
                  <span class="font-medium">{{ labelFor(pname, p) }}</span>
                  <code class="font-mono text-xs text-muted-foreground">{{ typeLabel(p) }}</code>
                  <Badge v-if="required.has(pname)" variant="outline" class="text-[10px]">required</Badge>
                </dt>
                <dd class="text-muted-foreground">
                  {{ p.description }}
                  <span v-if="p.default !== undefined" class="whitespace-nowrap"> · default <code class="font-mono text-xs text-foreground">{{ String(p.default) }}</code></span>
                </dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard v-if="can('knowledge.read')" :title="t.actions.knowledge" :icon="BookOpen">
            <ul v-if="linked.length" class="flex flex-col gap-1.5">
              <li v-for="h in linked" :key="h.uri">
                <RouterLink :to="entityPath(h.type!, h.slug!)" class="-mx-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-state-hover active:bg-state-press">
                  <BookOpen class="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" /> <span class="truncate">{{ h.title }}</span>
                </RouterLink>
              </li>
            </ul>
            <p v-else class="text-sm text-muted-foreground">{{ t.actions.knowledgeHint(tag) }}</p>
          </SectionCard>

          <SectionCard v-if="action.source" :title="t.actions.source" :icon="FileCode2">
            <CopyField :value="action.source" label="path" />
            <p class="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Code2 class="size-3.5" aria-hidden="true" /> Changed through reviewed pull requests.</p>
          </SectionCard>
        </aside>
      </div>
    </template>
  </div>
</template>
